import { createFilterOptions, type PopperProps, type SxProps, type Theme } from '@mui/material';
import type { KeyboardEvent, MouseEvent } from 'react';
import type { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { AllPermissions } from '../../auth/api';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { formatWorkEntryYearRange, getWorkEntryEndSortTimestamp } from '../../workEntries';
import { getCreditKey } from '../creditKey';
import {
    KEY_CREDITS_DISPLAY_MODE,
    KEY_CREDITS_DROPDOWN_MAX_WIDTH_EXTRA_PX,
    KEY_CREDITS_DROPDOWN_WIDTH_PADDING_PX,
    KEY_CREDITS_WRAP_LIMIT
} from '../constants';
import { formatRolesLabel } from '../roleFormatting';
import type {
    KeyCreditOption,
    KeyCreditsDisplayMode,
    KeyCreditsItemInfo,
    KeyCreditsLineItemRow,
    KeyCreditsWrapLimit
} from '../types';

/** Whether a shortlist line item row is a person line item. */
export const isPersonLineItem = (row: KeyCreditsLineItemRow | undefined): boolean =>
    Boolean(row?.isPerson || row?.itemReference?.type === ENTITY_CONSTANTS.person);

/**
 * Whether AI-extracted credits can be merged into the candidate's person profile.
 * Eligible for org-owned person contacts (not shared in directory) with Edit permission.
 * When security metadata is absent (e.g. net-new person in the line item form), eligibility
 * falls back to the other checks (person type/id and non-directory-shared).
 */
export const isProfileEligibleForCreditUpdate = (row: KeyCreditsLineItemRow | undefined): boolean => {
    if (!row) {
        return false;
    }

    // Only person profiles are eligible for candidate profile updates.
    if (row.itemReference?.type !== ENTITY_CONSTANTS.person) {
        return false;
    }

    const personId = row.itemReference.id;
    if (!personId) {
        return false;
    }

    if (row.isItemSharedInDirectory) {
        return false;
    }

    const allowedActions = row.security?.allowedActions;
    if (Array.isArray(allowedActions)) {
        return allowedActions.includes(AllPermissions.Edit);
    }

    return true;
};

/** Person entity id from line-item `itemInfo` (matches grid `itemReference.id`). */
export const getKeyCreditsPersonId = (
    itemInfo: { ref?: { id?: string }; id?: string } | null | undefined
): string | undefined => itemInfo?.ref?.id ?? itemInfo?.id;

/** Build a key-credits line item row from shortlist form `itemInfo` for eligibility/profile updates. */
export const toKeyCreditsLineItemRowFromItemInfo = (
    itemInfo: KeyCreditsItemInfo | null | undefined,
    options?: {
        lineItemEntityType?: string;
        lineItemSecurity?: { allowedActions?: string[] };
        isPerson?: boolean;
    }
): KeyCreditsLineItemRow | undefined => {
    const { lineItemEntityType, lineItemSecurity, isPerson: isPersonOverride } = options ?? {};

    if (lineItemEntityType !== ENTITY_CONSTANTS.person) {
        return undefined;
    }

    const itemReference = itemInfo?.ref;
    const referenceType = itemReference?.type;
    const resolvedPersonId = getKeyCreditsPersonId(itemInfo);
    const isPerson =
        isPersonOverride ??
        (referenceType !== ENTITY_CONSTANTS.organization &&
            (referenceType === ENTITY_CONSTANTS.person || Boolean(!referenceType && resolvedPersonId)));

    if (!isPerson || !resolvedPersonId) {
        return undefined;
    }

    return {
        name: itemInfo?.label || itemReference?.label || '',
        itemReference: {
            ...itemReference,
            id: resolvedPersonId,
            type: referenceType || ENTITY_CONSTANTS.person
        },
        isPerson: true,
        isItemSharedInDirectory: Boolean(itemInfo?.isSharedInDirectory),
        security: lineItemSecurity ?? itemInfo?.security
    };
};

export const keyCreditsClampBoxSx: SxProps<Theme> = {
    wordBreak: 'break-word',
    display: '-webkit-box',
    width: '100%',
    cursor: 'pointer'
};

/** Title string for chips / filters (show label or name). */
export const getCreditTitle = (credit: CreativeCredit | null | undefined): string => {
    if (!credit) return '';
    return (credit.showReference?.label || credit.showName || '').trim();
};

export const formatKeyCredit = (credit: CreativeCredit | null | undefined): string => {
    if (!credit) return '';
    const title = getCreditTitle(credit);
    if (!title) return '';
    const rolesLabel = formatRolesLabel(credit.professionalRoles);
    return rolesLabel ? `${rolesLabel}: ${title}` : title;
};

export const formatKeyCreditOptionSubLabel = (credit: CreativeCredit | null | undefined): string => {
    if (!credit) return '';
    const parts: string[] = [];
    const showType = (credit.showType || '').trim();
    if (showType) parts.push(showType);
    const yearRange = formatWorkEntryYearRange(credit);
    if (yearRange) parts.push(yearRange);
    return parts.join(', ');
};

export const formatKeyCreditsDisplayText = (
    labels: ReadonlyArray<string>,
    displayMode: KeyCreditsDisplayMode = KEY_CREDITS_DISPLAY_MODE.lineBreak
): string => {
    if (!labels.length) return '';
    switch (displayMode) {
        case KEY_CREDITS_DISPLAY_MODE.pipe:
            return labels.join(' | ');
        case KEY_CREDITS_DISPLAY_MODE.lineBreak:
            return labels.join('\n');
        case KEY_CREDITS_DISPLAY_MODE.comma:
        case KEY_CREDITS_DISPLAY_MODE.chip:
            return labels.join(', ');
    }
};

export const clampKeyCreditsWrapLimit = (value: number | undefined): KeyCreditsWrapLimit => {
    const resolved = value ?? KEY_CREDITS_WRAP_LIMIT.default;
    const numeric = Number.isFinite(resolved) ? resolved : KEY_CREDITS_WRAP_LIMIT.default;
    return Math.min(
        KEY_CREDITS_WRAP_LIMIT.max,
        Math.max(KEY_CREDITS_WRAP_LIMIT.min, Math.round(numeric))
    ) as KeyCreditsWrapLimit;
};

export const KEY_CREDITS_COLUMN_MENU_WRAP_LIMIT_OPTIONS = Array.from(
    { length: KEY_CREDITS_WRAP_LIMIT.max - KEY_CREDITS_WRAP_LIMIT.min + 1 },
    (_, index) => (KEY_CREDITS_WRAP_LIMIT.min + index) as KeyCreditsWrapLimit
);

export const isKeyCreditsColumnField = (field: string | undefined, columnNames: string[]): boolean =>
    !!field && columnNames.includes(field);

export const stopKeyCreditsColumnMenuSelectKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === ' ') {
        event.stopPropagation();
    }
};

export const stopKeyCreditsColumnMenuItemClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
};

export const KEY_CREDITS_COLUMN_MENU_POPPER_MODIFIERS = [
    {
        name: 'preventOverflow',
        enabled: true,
        options: {
            boundary: 'viewport',
            rootBoundary: 'viewport',
            padding: 8,
            altAxis: true
        }
    },
    {
        name: 'flip',
        enabled: true,
        options: {
            fallbackPlacements: ['top-end', 'top-start', 'bottom-end', 'bottom-start']
        }
    }
] as const;

export const toKeyCreditOption = (credit: CreativeCredit, sourceIndex: number): KeyCreditOption => {
    const primary = formatKeyCredit(credit) || getCreditTitle(credit);
    const subLabel = formatKeyCreditOptionSubLabel(credit);
    return {
        key: getCreditKey(credit, sourceIndex),
        label: primary,
        ...(subLabel ? { subLabel } : {}),
        credit
    };
};

export const filterKeyCreditOptions = createFilterOptions<KeyCreditOption>();

export const isFocusWithinAutocomplete = (element: Element | null) =>
    Boolean(element?.closest('.MuiAutocomplete-root, .MuiAutocomplete-popper'));

export const hasVisibleKeyCreditOptions = (
    options: KeyCreditOption[],
    inputValue: string,
    selectedValue: KeyCreditOption[]
) => {
    if (options.length === 0) return false;
    const selectedKeys = new Set(selectedValue.map((opt) => opt.key));
    return filterKeyCreditOptions(options, {
        inputValue,
        getOptionLabel: (opt) => opt.label
    }).some((opt) => !selectedKeys.has(opt.key));
};

export const shouldShowKeyCreditAddOption = (options: KeyCreditOption[], inputValue: string): boolean => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    return !options.some((option) => getCreditTitle(option.credit).toLowerCase() === trimmed.toLowerCase());
};

export const buildSelectedKeyCreditOptions = (
    selectedCredits: CreativeCredit[],
    personCredits: CreativeCredit[]
): KeyCreditOption[] =>
    selectedCredits.map((credit, selIdx) => {
        const baseKey = getCreditKey(credit);
        let sourceIndex = personCredits.findIndex((p) => p && getCreditKey(p) === baseKey);
        if (sourceIndex < 0) {
            sourceIndex = selIdx;
        }
        return toKeyCreditOption(credit, sourceIndex);
    });

export const keyCreditsDropdownPopperModifiers: PopperProps['modifiers'] = [
    {
        name: 'keyCreditsDropdownWidth',
        enabled: true,
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn({ state }) {
            const columnWidth = state.rects.reference.width;
            const minWidth = Math.round(columnWidth + KEY_CREDITS_DROPDOWN_WIDTH_PADDING_PX);
            Object.assign(state.styles.popper, {
                minWidth: `${minWidth}px`,
                maxWidth: `${minWidth + KEY_CREDITS_DROPDOWN_MAX_WIDTH_EXTRA_PX}px`,
                width: 'auto'
            });
        }
    }
];

export const keyCreditsFormDropdownPopperModifiers: PopperProps['modifiers'] = [
    {
        name: 'keyCreditsFormDropdownWidth',
        enabled: true,
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn({ state }) {
            const inputWidth = Math.round(state.rects.reference.width);
            Object.assign(state.styles.popper, {
                minWidth: `${inputWidth}px`,
                maxWidth: `${inputWidth}px`,
                width: `${inputWidth}px`
            });
        }
    }
];

export const buildKeyCreditOptions = (
    personCredits: ReadonlyArray<CreativeCredit | null | undefined>
): KeyCreditOption[] => {
    const indexed = personCredits
        .map((credit, sourceIndex) => ({
            credit,
            sourceIndex,
            endTs: credit ? getWorkEntryEndSortTimestamp(credit) : 0
        }))
        .filter(
            (entry): entry is { credit: CreativeCredit; sourceIndex: number; endTs: number } =>
                Boolean(entry.credit) && Boolean(getCreditTitle(entry.credit))
        );

    return [...indexed]
        .sort((a, b) => b.endTs - a.endTs)
        .map(({ credit, sourceIndex }) => toKeyCreditOption(credit, sourceIndex));
};

const matchesKeyCreditTitle = (credit: CreativeCredit, term: string): boolean =>
    getCreditTitle(credit).toLowerCase() === term.trim().toLowerCase();

export const canAddFreeTextKeyCreditOption = (
    options: KeyCreditOption[],
    selectedValue: KeyCreditOption[],
    inputValue: string
): boolean => {
    const term = inputValue.trim();
    if (!term) return false;
    const matchesExisting = options.some((opt) => matchesKeyCreditTitle(opt.credit, term));
    const alreadySelected = selectedValue.some((opt) => matchesKeyCreditTitle(opt.credit, term));
    return !matchesExisting && !alreadySelected;
};

export const buildFreeTextAddKeyCreditOption = (term: string, addLabel: string): KeyCreditOption => ({
    key: `free-text:${term}`,
    label: addLabel,
    credit: {
        showName: term,
        showType: '',
        department: '',
        professionalRoles: []
    } as CreativeCredit,
    isFreeTextAdd: true
});

export const mergeKeyCredits = (existing: CreativeCredit[], selectedCredits: CreativeCredit[]): CreativeCredit[] => {
    const seen = new Set(existing.map((credit) => getCreditKey(credit)));
    const mergedKeyCredits = [...existing];
    for (const credit of selectedCredits) {
        const key = getCreditKey(credit);
        if (seen.has(key)) continue;
        seen.add(key);
        mergedKeyCredits.push(credit);
    }
    return mergedKeyCredits;
};
